import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  message,
  Drawer,
  Avatar,
  Divider,
  Badge,
  Steps,
  Tree,
  Tooltip,
  Popconfirm,
  Radio,
  InputNumber,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  TeamOutlined,
  BankOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
  UploadOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  SearchOutlined,
  FilterOutlined,
  SettingOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;
const { TreeNode } = Tree;
const { TextArea } = Input;

interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  hireDate: string;
  status: 'active' | 'inactive' | 'on-leave' | 'terminated';
  salary: number;
  employmentType: string;
  workLocation?: string;
  managerId?: string;
  grade?: string;
  address?: string;
  dateOfBirth?: string;
  nationality?: string;
  maritalStatus?: string;
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Department {
  id: string;
  name: string;
  parentId?: string;
  managerId?: string;
  employeeCount: number;
  isActive: boolean;
  children?: Department[];
}

interface Designation {
  id: string;
  title: string;
  departmentId: string;
  description: string;
  isActive: boolean;
  employees: number;
}

interface EmployeeGrade {
  id: string;
  name: string;
  level: number;
  minSalary: number;
  maxSalary: number;
  benefits: string[];
}

interface EmploymentType {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

const EmployeeManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [employeeGrades, setEmployeeGrades] = useState<EmployeeGrade[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
  const [isDepartmentModalVisible, setIsDepartmentModalVisible] = useState(false);
  const [isDesignationModalVisible, setIsDesignationModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    position: '',
    status: '',
    employmentType: ''
  });
  const [form] = Form.useForm();
  const [departmentForm] = Form.useForm();
  const [designationForm] = Form.useForm();

  // Sample data
  const sampleEmployees: Employee[] = [
    {
      id: '1',
      employeeId: 'EMP001',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@company.com',
      phone: '+1234567890',
      department: 'Engineering',
      position: 'Senior Software Engineer',
      hireDate: '2023-01-15',
      status: 'active',
      salary: 85000,
      employmentType: 'full-time',
      workLocation: 'hybrid',
      managerId: 'EMP005',
      grade: 'L4',
      address: '123 Main St, City, State, 12345',
      dateOfBirth: '1990-05-15',
      nationality: 'American',
      maritalStatus: 'single',
      emergencyContact: {
        name: 'Jane Doe',
        relationship: 'Sister',
        phone: '+1234567899'
      },
      createdAt: '2023-01-15T10:00:00Z',
      updatedAt: '2023-01-15T10:00:00Z'
    },
    {
      id: '2',
      employeeId: 'EMP002',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@company.com',
      phone: '+1234567891',
      department: 'Marketing',
      position: 'Marketing Manager',
      hireDate: '2023-02-01',
      status: 'active',
      salary: 75000,
      employmentType: 'full-time',
      workLocation: 'office',
      grade: 'L3',
      createdAt: '2023-02-01T09:00:00Z',
      updatedAt: '2023-02-01T09:00:00Z'
    }
  ];

  const sampleDepartments: Department[] = [
    {
      id: '1',
      name: 'Engineering',
      managerId: 'EMP005',
      employeeCount: 45,
      isActive: true,
      children: [
        { id: '11', name: 'Frontend Team', parentId: '1', employeeCount: 15, isActive: true },
        { id: '12', name: 'Backend Team', parentId: '1', employeeCount: 20, isActive: true },
        { id: '13', name: 'DevOps Team', parentId: '1', employeeCount: 10, isActive: true }
      ]
    },
    {
      id: '2',
      name: 'Sales',
      managerId: 'EMP006',
      employeeCount: 32,
      isActive: true,
      children: [
        { id: '21', name: 'Inside Sales', parentId: '2', employeeCount: 20, isActive: true },
        { id: '22', name: 'Field Sales', parentId: '2', employeeCount: 12, isActive: true }
      ]
    },
    {
      id: '3',
      name: 'Marketing',
      managerId: 'EMP007',
      employeeCount: 18,
      isActive: true
    },
    {
      id: '4',
      name: 'HR',
      managerId: 'EMP008',
      employeeCount: 8,
      isActive: true
    }
  ];

  const sampleDesignations: Designation[] = [
    {
      id: '1',
      title: 'Senior Software Engineer',
      departmentId: '1',
      description: 'Responsible for designing and implementing software solutions',
      isActive: true,
      employees: 15
    },
    {
      id: '2',
      title: 'Marketing Manager',
      departmentId: '3',
      description: 'Manages marketing campaigns and strategies',
      isActive: true,
      employees: 5
    },
    {
      id: '3',
      title: 'Sales Representative',
      departmentId: '2',
      description: 'Manages client relationships and sales activities',
      isActive: true,
      employees: 25
    }
  ];

  const sampleEmployeeGrades: EmployeeGrade[] = [
    {
      id: '1',
      name: 'L1 - Junior',
      level: 1,
      minSalary: 40000,
      maxSalary: 60000,
      benefits: ['Health Insurance', 'Basic PTO']
    },
    {
      id: '2',
      name: 'L2 - Mid-level',
      level: 2,
      minSalary: 60000,
      maxSalary: 80000,
      benefits: ['Health Insurance', 'Extended PTO', 'Dental']
    },
    {
      id: '3',
      name: 'L3 - Senior',
      level: 3,
      minSalary: 80000,
      maxSalary: 120000,
      benefits: ['Health Insurance', 'Extended PTO', 'Dental', 'Vision', '401k Match']
    },
    {
      id: '4',
      name: 'L4 - Principal',
      level: 4,
      minSalary: 120000,
      maxSalary: 160000,
      benefits: ['Health Insurance', 'Extended PTO', 'Dental', 'Vision', '401k Match', 'Stock Options']
    }
  ];

  const sampleEmploymentTypes: EmploymentType[] = [
    { id: '1', name: 'Full-time', description: 'Regular full-time employee', isActive: true },
    { id: '2', name: 'Part-time', description: 'Part-time employee', isActive: true },
    { id: '3', name: 'Contract', description: 'Contract worker', isActive: true },
    { id: '4', name: 'Intern', description: 'Internship position', isActive: true },
    { id: '5', name: 'Consultant', description: 'External consultant', isActive: true }
  ];

  useEffect(() => {
    setEmployees(sampleEmployees);
    setDepartments(sampleDepartments);
    setDesignations(sampleDesignations);
    setEmployeeGrades(sampleEmployeeGrades);
    setEmploymentTypes(sampleEmploymentTypes);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'on-leave': return 'orange';
      case 'terminated': return 'red';
      default: return 'default';
    }
  };

  const handleAddEmployee = async (values: any) => {
    try {
      setLoading(true);
      // API call would go here
      const newEmployee: Employee = {
        id: Date.now().toString(),
        employeeId: `EMP${(employees.length + 1).toString().padStart(3, '0')}`,
        ...values,
        hireDate: values.hireDate?.format('YYYY-MM-DD'),
        dateOfBirth: values.dateOfBirth?.format('YYYY-MM-DD'),
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setEmployees([...employees, newEmployee]);
      setIsEmployeeModalVisible(false);
      form.resetFields();
      message.success('Employee added successfully');
    } catch (error) {
      message.error('Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async (values: any) => {
    try {
      setLoading(true);
      const newDepartment: Department = {
        id: Date.now().toString(),
        ...values,
        employeeCount: 0,
        isActive: true
      };
      setDepartments([...departments, newDepartment]);
      setIsDepartmentModalVisible(false);
      departmentForm.resetFields();
      message.success('Department added successfully');
    } catch (error) {
      message.error('Failed to add department');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDesignation = async (values: any) => {
    try {
      setLoading(true);
      const newDesignation: Designation = {
        id: Date.now().toString(),
        ...values,
        employees: 0,
        isActive: true
      };
      setDesignations([...designations, newDesignation]);
      setIsDesignationModalVisible(false);
      designationForm.resetFields();
      message.success('Designation added successfully');
    } catch (error) {
      message.error('Failed to add designation');
    } finally {
      setLoading(false);
    }
  };

  const employeeColumns: ColumnsType<Employee> = [
    {
      title: 'Employee ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      sorter: true,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div>{`${record.firstName} ${record.lastName}`}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      filters: departments.map(dept => ({ text: dept.name, value: dept.name }))
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position'
    },
    {
      title: 'Employment Type',
      dataIndex: 'employmentType',
      key: 'employmentType',
      render: (type) => <Tag>{type}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
    },
    {
      title: 'Hire Date',
      dataIndex: 'hireDate',
      key: 'hireDate',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => setSelectedEmployee(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to deactivate this employee?"
            onConfirm={() => {}}
          >
            <Tooltip title="Deactivate">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const departmentColumns: ColumnsType<Department> = [
    {
      title: 'Department Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <ApartmentOutlined />
          <span>{text}</span>
          {!record.isActive && <Tag color="red">Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Manager',
      dataIndex: 'managerId',
      key: 'managerId',
      render: (managerId) => {
        const manager = employees.find(emp => emp.id === managerId);
        return manager ? `${manager.firstName} ${manager.lastName}` : 'Not Assigned';
      }
    },
    {
      title: 'Employee Count',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
      render: (count) => <Badge count={count} showZero color="blue" />
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} danger />
        </Space>
      )
    }
  ];

  const designationColumns: ColumnsType<Designation> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: 'Department',
      dataIndex: 'departmentId',
      key: 'departmentId',
      render: (deptId) => {
        const dept = departments.find(d => d.id === deptId);
        return dept?.name || 'Unknown';
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Employees',
      dataIndex: 'employees',
      key: 'employees',
      render: (count) => <Badge count={count} showZero color="blue" />
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="text" icon={<EditOutlined />} />
          <Button type="text" icon={<DeleteOutlined />} danger />
        </Space>
      )
    }
  ];

  const renderOrgChart = () => {
    const treeData = departments.map(dept => ({
      title: (
        <Space>
          <ApartmentOutlined />
          <span>{dept.name}</span>
          <Badge count={dept.employeeCount} />
        </Space>
      ),
      key: dept.id,
      children: dept.children?.map(child => ({
        title: (
          <Space>
            <TeamOutlined />
            <span>{child.name}</span>
            <Badge count={child.employeeCount} />
          </Space>
        ),
        key: child.id
      }))
    }));

    return (
      <Card title="Organizational Chart">
        <Tree
          showLine
          defaultExpandAll
          treeData={treeData}
          onSelect={(selectedKeys) => {
            console.log('Selected:', selectedKeys);
          }}
        />
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Title level={2}>Employee Management</Title>
        <Space>
          <Button icon={<DownloadOutlined />}>Export</Button>
          <Button icon={<UploadOutlined />}>Import</Button>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />}
            onClick={() => setIsEmployeeModalVisible(true)}
          >
            Add Employee
          </Button>
        </Space>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Employees" key="employees">
          <Card>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Search employees..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Department"
                  style={{ width: '100%' }}
                  value={filters.department}
                  onChange={(value) => setFilters({ ...filters, department: value })}
                  allowClear
                >
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.name}>{dept.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Status"
                  style={{ width: '100%' }}
                  value={filters.status}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  allowClear
                >
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="on-leave">On Leave</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button type="primary" icon={<FilterOutlined />}>
                  Advanced Filters
                </Button>
              </Col>
            </Row>

            <Table
              columns={employeeColumns}
              dataSource={employees.filter(emp => {
                const matchesSearch = searchText === '' || 
                  `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchText.toLowerCase()) ||
                  emp.employeeId.toLowerCase().includes(searchText.toLowerCase());
                const matchesDepartment = filters.department === '' || emp.department === filters.department;
                const matchesStatus = filters.status === '' || emp.status === filters.status;
                return matchesSearch && matchesDepartment && matchesStatus;
              })}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Departments" key="departments">
          <Card
            title="Departments"
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setIsDepartmentModalVisible(true)}
              >
                Add Department
              </Button>
            }
          >
            <Table
              columns={departmentColumns}
              dataSource={departments}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Designations" key="designations">
          <Card
            title="Job Positions"
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setIsDesignationModalVisible(true)}
              >
                Add Designation
              </Button>
            }
          >
            <Table
              columns={designationColumns}
              dataSource={designations}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Employee Grades" key="grades">
          <Card title="Employee Grades">
            <Row gutter={[16, 16]}>
              {employeeGrades.map(grade => (
                <Col xs={24} sm={12} md={8} lg={6} key={grade.id}>
                  <Card size="small">
                    <Title level={5}>{grade.name}</Title>
                    <p>Level: {grade.level}</p>
                    <p>Salary: ${grade.minSalary.toLocaleString()} - ${grade.maxSalary.toLocaleString()}</p>
                    <Divider />
                    <Text strong>Benefits:</Text>
                    <ul style={{ marginTop: 8 }}>
                      {grade.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Employment Types" key="employment-types">
          <Card title="Employment Types">
            <Table
              dataSource={employmentTypes}
              rowKey="id"
              pagination={false}
              columns={[
                { title: 'Type', dataIndex: 'name', key: 'name' },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                {
                  title: 'Status',
                  dataIndex: 'isActive',
                  key: 'isActive',
                  render: (isActive) => (
                    <Tag color={isActive ? 'green' : 'red'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Tag>
                  )
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: () => (
                    <Space>
                      <Button type="text" icon={<EditOutlined />} />
                      <Button type="text" icon={<DeleteOutlined />} danger />
                    </Space>
                  )
                }
              ]}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Org Chart" key="org-chart">
          {renderOrgChart()}
        </Tabs.TabPane>
      </Tabs>

      {/* Add Employee Modal */}
      <Modal
        title="Add New Employee"
        open={isEmployeeModalVisible}
        onCancel={() => {
          setIsEmployeeModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleAddEmployee}>
          <Title level={5}>Personal Information</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dateOfBirth" label="Date of Birth">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nationality" label="Nationality">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address">
            <TextArea rows={2} />
          </Form.Item>

          <Divider />
          <Title level={5}>Employment Details</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="department" label="Department" rules={[{ required: true }]}>
                <Select>
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.name}>{dept.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label="Position" rules={[{ required: true }]}>
                <Select>
                  {designations.map(designation => (
                    <Option key={designation.id} value={designation.title}>
                      {designation.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employmentType" label="Employment Type" rules={[{ required: true }]}>
                <Select>
                  {employmentTypes.map(type => (
                    <Option key={type.id} value={type.name}>{type.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hireDate" label="Hire Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="salary" label="Annual Salary" rules={[{ required: true }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="workLocation" label="Work Location">
                <Select>
                  <Option value="office">Office</Option>
                  <Option value="remote">Remote</Option>
                  <Option value="hybrid">Hybrid</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Employee
              </Button>
              <Button onClick={() => {
                setIsEmployeeModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Department Modal */}
      <Modal
        title="Add New Department"
        open={isDepartmentModalVisible}
        onCancel={() => {
          setIsDepartmentModalVisible(false);
          departmentForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={departmentForm} layout="vertical" onFinish={handleAddDepartment}>
          <Form.Item name="name" label="Department Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label="Parent Department">
            <Select placeholder="Select parent department (optional)">
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="managerId" label="Department Manager">
            <Select placeholder="Select manager">
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>
                  {`${emp.firstName} ${emp.lastName}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Department
              </Button>
              <Button onClick={() => {
                setIsDepartmentModalVisible(false);
                departmentForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Designation Modal */}
      <Modal
        title="Add New Designation"
        open={isDesignationModalVisible}
        onCancel={() => {
          setIsDesignationModalVisible(false);
          designationForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={designationForm} layout="vertical" onFinish={handleAddDesignation}>
          <Form.Item name="title" label="Designation Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="departmentId" label="Department" rules={[{ required: true }]}>
            <Select>
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Designation
              </Button>
              <Button onClick={() => {
                setIsDesignationModalVisible(false);
                designationForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Employee Details Drawer */}
      <Drawer
        title="Employee Details"
        placement="right"
        width={600}
        open={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
      >
        {selectedEmployee && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar size={80} icon={<UserOutlined />} />
              <Title level={4} style={{ marginTop: 16 }}>
                {`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
              </Title>
              <Text type="secondary">{selectedEmployee.position}</Text>
            </div>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong>Employee ID:</Text>
                <div>{selectedEmployee.employeeId}</div>
              </Col>
              <Col span={12}>
                <Text strong>Department:</Text>
                <div>{selectedEmployee.department}</div>
              </Col>
              <Col span={12}>
                <Text strong>Email:</Text>
                <div>{selectedEmployee.email}</div>
              </Col>
              <Col span={12}>
                <Text strong>Phone:</Text>
                <div>{selectedEmployee.phone}</div>
              </Col>
              <Col span={12}>
                <Text strong>Hire Date:</Text>
                <div>{dayjs(selectedEmployee.hireDate).format('MMM DD, YYYY')}</div>
              </Col>
              <Col span={12}>
                <Text strong>Employment Type:</Text>
                <div>{selectedEmployee.employmentType}</div>
              </Col>
              <Col span={12}>
                <Text strong>Work Location:</Text>
                <div>{selectedEmployee.workLocation}</div>
              </Col>
              <Col span={12}>
                <Text strong>Status:</Text>
                <div>
                  <Tag color={getStatusColor(selectedEmployee.status)}>
                    {selectedEmployee.status.toUpperCase()}
                  </Tag>
                </div>
              </Col>
            </Row>

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button icon={<EditOutlined />}>Edit</Button>
              <Button icon={<PrinterOutlined />}>Print</Button>
              <Button icon={<FileExcelOutlined />}>Export</Button>
            </Space>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default EmployeeManagement; 